import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_API_KEYS_PATH } from '@hooks/requests/utils/paths';

export interface CreateApiKeyParams {
  name: string;
}

/**
 * Response for POST /api/v1/partner/api-keys (sc-45103, AC11).
 *
 * `apiKey` is the new key's Auth0 client_secret (each key is its own M2M
 * Application, authenticated with a standard client_secret — NOT Private
 * Key JWT, per sc-45102's Sep 2 update), shown to the admin exactly once,
 * in the follow-up modal (AC11/AC12) — nothing on this backend persists
 * it, and this hook does not cache it either (it is not part of the
 * invalidated `PARTNER_API_KEYS_PATH` list query's shape).
 */
export interface CreateApiKeyResponse {
  id: string;
  name: string | null;
  apiKey: string;
}

export const useCreateApiKeyMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name }: CreateApiKeyParams): Promise<CreateApiKeyResponse> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      return fetchJson<CreateApiKeyResponse>(
        joinUrl(allyApi, PARTNER_API_KEYS_PATH),
        authHeader,
        'Failed to create the API key',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_PATH] });
    },
  });
};

export default useCreateApiKeyMutation;
